import { Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { IUsuarioDropdownItem, IAnalistaListItem, IAnalistaDetailRequest } from './interfaces/analistas.interface';
import { AnalistasService } from './services/analistas.service';
import { MatDialog } from '@angular/material/dialog';
import { AbcEmpresasCustomizablesComponent } from '@p-parametria/mantenimiento/abc-empresas/components/abc-empresas-customizables/abc-empresas-customizables.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-analista-form',
  templateUrl: './analista-form.component.html',
  providers: [MessageService]
})
export class AnalistaFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() model: IAnalistaListItem | null = null;
  @Output() onSave = new EventEmitter<{ success: boolean; message: string }>();
  form!: FormGroup;
  loading: boolean = false;
  usuariosDropdown: IUsuarioDropdownItem[] = [];
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private analistasService: AnalistasService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadUsuariosDropdown();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['model']) {
      if (changes['model'].currentValue) {
        this.loadAnalistaDetail(this.model.susuario);
      } else {
        this.initForm();
        this.form.reset();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initForm(): void {
    this.form = this.fb.group({
      clave_analista: [null, [Validators.required]],
      nombre_analista: [{ value: '', disabled: true }, [Validators.required]]
    });

    // Suscribirse a cambios en el campo clave_analista
    this.form.get('clave_analista')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value) {
          this.onAnalistaSelected(value);
        }
      });
  }

  onAnalistaSelected(susuario: string): void {
    const selectedUser = this.usuariosDropdown.find(user => user.susuario === susuario);
    if (selectedUser) {
      this.form.patchValue({
        nombre_analista: selectedUser.nombreusuario
      });
    }
  }

  loadAnalistaDetail(susuario: string): void {
    this.loading = true;
    const payload: IAnalistaDetailRequest = {
      que: 5,
      tope: '',
      cmd: '',
      clave_analista: susuario,
      nombre_analista: ''
    };
    this.analistasService.getAnalistaDetail(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success && response.data.length > 0) {
            const detalle = response.data[0];
            this.form.patchValue({
              clave_analista: detalle.susuario,
              nombre_analista: detalle.nombre_analista
            });
            this.form.get('clave_analista')?.disable();
            this.form.get('nombre_analista')?.disable();
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'No se pudo cargar el detalle del analista'
            });
          }
          this.loading = false;
        },
        error: (error) => {
          console.error('Error al cargar detalle de analista:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar el detalle del analista'
          });
          this.loading = false;
        }
      });
  }

  loadUsuariosDropdown(): void {
    this.analistasService.getUsuariosDropdown({ tope: '9999', usuario: '' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.usuariosDropdown = response.data;
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'No se pudieron cargar los usuarios'
            });
          }
        },
        error: (error) => {
          console.error('Error al cargar usuarios para el dropdown:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar los usuarios'
          });
        }
      });
  }

  create(): void {
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor complete todos los campos requeridos'
      });
      return;
    }

    this.loading = true;
    const formData = this.form.getRawValue();
    const payload = {
      que: 1,
      tope: '',
      cmd: '',
      clave_analista: formData.clave_analista,
      nombre_analista: formData.nombre_analista
    };

    this.analistasService.addAnalista(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.onSave.emit({
              success: true,
              message: 'Analista creado correctamente'
            });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Error al crear el analista'
            });
          }
          this.loading = false;
        },
        error: (error) => {
          console.error('Error al crear analista:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al crear el analista'
          });
          this.loading = false;
        }
      });
  }

  update(): void {
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor complete todos los campos requeridos'
      });
      return;
    }

    this.loading = true;
    const formData = this.form.getRawValue();
    const payload = {
      que: 2,
      tope: '',
      cmd: '',
      clave_analista: this.model.susuario,
      nombre_analista: formData.nombre_analista
    };

    this.analistasService.updateAnalista(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.onSave.emit({
              success: true,
              message: 'Analista actualizado correctamente'
            });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Error al actualizar el analista'
            });
          }
          this.loading = false;
        },
        error: (error) => {
          console.error('Error al actualizar analista:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al actualizar el analista'
          });
          this.loading = false;
        }
      });
  }

  openCustomizables(): void {
    this.dialog.open(AbcEmpresasCustomizablesComponent, {
      width: '1000px',
      height: '1000px',
      disableClose: true,
      panelClass: ['custom-dialog-zindex'],
      hasBackdrop: true,
      autoFocus: false,
      data: {
        sTabla: 'analistas',
        sClave: `${this.form.get('clave_analista')?.value}`,
        sNombre: this.form.get('nombre_analista')?.value,
        sTipo: 'Opcion'
      }
    });
  }
} 